import { OrderStatus, OrderStatusLabel } from "@/types/enums/OrderStatus"

export type OrderWorkflowStepVisualState = "completed" | "current" | "pending"

export type OrderWorkflowStepDefinition = {
  key: string
  label: string
}

/** Stable workflow stage identifiers — unchanged for analytics / legacy stage keys. */
export enum WorkflowStage {
  Placed = "PLACED",
  /** Internal stage key; customer-facing label is "Restaurant accepted". */
  Accepted = "ACCEPTED",
  Preparing = "PREPARING",
  Ready = "READY",
  Shipped = "SHIPPED",
  Delivered = "DELIVERED",
}

/** Canonical order lifecycle stages shown in the workflow stepper. */
export const ORDER_WORKFLOW_STEPS: readonly OrderWorkflowStepDefinition[] = [
  { key: WorkflowStage.Placed, label: "Order received" },
  { key: WorkflowStage.Accepted, label: "Restaurant accepted" },
  { key: WorkflowStage.Preparing, label: "Preparing" },
  { key: WorkflowStage.Ready, label: "Ready" },
  { key: WorkflowStage.Shipped, label: "Out for delivery" },
  { key: WorkflowStage.Delivered, label: "Delivered" },
] as const

/**
 * Pickup (persisted `order_type_id` 2) presentation of the same backend lifecycle. No new
 * statuses: Ready reads "Ready for pickup", Completed / Delivered read "Collected", and the
 * legacy dispatch statuses some old Takeaway rows carry stay on "Ready for pickup".
 */
export const PICKUP_WORKFLOW_STEPS: readonly OrderWorkflowStepDefinition[] = [
  { key: WorkflowStage.Placed, label: "Order received" },
  { key: WorkflowStage.Accepted, label: "Restaurant accepted" },
  { key: WorkflowStage.Preparing, label: "Preparing" },
  { key: WorkflowStage.Ready, label: "Ready for pickup" },
  { key: WorkflowStage.Delivered, label: "Collected" },
] as const

/** Index of "Ready for pickup" in {@link PICKUP_WORKFLOW_STEPS}. */
const PICKUP_READY_STEP_INDEX = 3

/** Presentation options; omitted = Delivery (the existing behaviour, unchanged). */
export type OrderWorkflowOptions = {
  isPickup?: boolean
}

function workflowStepsFor(options?: OrderWorkflowOptions): readonly OrderWorkflowStepDefinition[] {
  return options?.isPickup ? PICKUP_WORKFLOW_STEPS : ORDER_WORKFLOW_STEPS
}

export type OrderWorkflowStageIndex = 0 | 1 | 2 | 3 | 4| 5

const TERMINAL_STATUSES = new Set<OrderStatus>([
  OrderStatus.Cancelled,
  OrderStatus.Returned,
  OrderStatus.Refunded,
])

const COMPLETE_STATUSES = new Set<OrderStatus>([OrderStatus.Delivered, OrderStatus.Completed])

const WORKFLOW_STAGE_INDEX: Record<WorkflowStage, OrderWorkflowStageIndex> = {
  [WorkflowStage.Placed]: 0,
  [WorkflowStage.Accepted]: 1,
  [WorkflowStage.Preparing]: 2,
  [WorkflowStage.Ready]: 3,
  [WorkflowStage.Shipped]: 4,
  [WorkflowStage.Delivered]: 5,
}

const TERMINAL_STATUS_MESSAGE: Readonly<Partial<Record<OrderStatus, string>>> = {
  [OrderStatus.Cancelled]: "Order was cancelled",
  [OrderStatus.Returned]: "Order was returned",
  [OrderStatus.Refunded]: "Refund processed",
}

export type OrderWorkflowPresentation =
  | { mode: "workflow"; workflowStage: WorkflowStage }
  | { mode: "terminal"; terminalStatus: OrderStatus }

export function isOrderTerminalStatus(
  orderStatus: OrderStatus
): orderStatus is OrderStatus.Cancelled | OrderStatus.Returned | OrderStatus.Refunded {
  return TERMINAL_STATUSES.has(orderStatus)
}

export function getOrderTerminalStatusLabel(terminalStatus: OrderStatus): string {
  return OrderStatusLabel[terminalStatus] ?? "Unknown"
}

export function getOrderTerminalStatusMessage(terminalStatus: OrderStatus): string {
  return TERMINAL_STATUS_MESSAGE[terminalStatus] ?? "Order status update"
}

function orderStatusToWorkflowStage(orderStatus: OrderStatus): WorkflowStage {
  switch (orderStatus) {
    case OrderStatus.Pending:
      // Backend Confirmed / OrderCreated (status_id 1)
      return WorkflowStage.Placed
    case OrderStatus.Confirmed:
      // Backend Accepted / RestaurantAccepted (status_id 2)
      return WorkflowStage.Accepted
    case OrderStatus.Preparing:
    case OrderStatus.Packaging:
      return WorkflowStage.Preparing
    case OrderStatus.Ready:
         return WorkflowStage.Ready
    case OrderStatus.ReadyToShip:
    case OrderStatus.Shipped:
    case OrderStatus.OutForDelivery:
      return WorkflowStage.Shipped
    case OrderStatus.Delivered:
    case OrderStatus.Completed:
      return WorkflowStage.Delivered
    case OrderStatus.Unknown:
    default:
      return WorkflowStage.Placed
  }
}

/**
 * Top-level presentation decision for order workflow UI.
 * Keeps components free of status branching.
 */
export function getOrderWorkflowPresentation(
  orderStatus: OrderStatus | null | undefined
): OrderWorkflowPresentation {
  const status = orderStatus ?? OrderStatus.Unknown

  if (isOrderTerminalStatus(status)) {
    return { mode: "terminal", terminalStatus: status }
  }

  return { mode: "workflow", workflowStage: orderStatusToWorkflowStage(status) }
}

type OrderWorkflowResolution =
  | { kind: "active"; stageIndex: OrderWorkflowStageIndex }
  | { kind: "complete" }
  | { kind: "terminal"; status: OrderStatus }

/**
 * Maps {@link OrderStatus} → workflow stage index.
 * Centralized mapping — do not duplicate in components.
 */
export function resolveOrderWorkflowStage(orderStatus: OrderStatus): OrderWorkflowResolution {
  if (TERMINAL_STATUSES.has(orderStatus)) {
    return { kind: "terminal", status: orderStatus }
  }
  if (COMPLETE_STATUSES.has(orderStatus)) {
    return { kind: "complete" }
  }

  const stage = orderStatusToWorkflowStage(orderStatus)
  return { kind: "active", stageIndex: WORKFLOW_STAGE_INDEX[stage] }
}

export type OrderWorkflowStepState = OrderWorkflowStepDefinition & {
  state: OrderWorkflowStepVisualState
}

export type OrderWorkflowProgress = {
  steps: OrderWorkflowStepState[]
  isComplete: boolean
  /** Index of the current step, or `steps.length` when the journey is complete. */
  activeStepIndex: number
}

/** Stable icon keys for workflow stages — map to SVG components in the view layer. */
export type WorkflowStageIconKey = "order" | "confirmed" | "preparing" | "ready" | "delivery" | "delivered"

export const WORKFLOW_STAGE_ICON_KEY: Readonly<Record<WorkflowStage, WorkflowStageIconKey>> = {
  [WorkflowStage.Placed]: "order",
  [WorkflowStage.Accepted]: "confirmed",
  [WorkflowStage.Preparing]: "preparing",
  [WorkflowStage.Ready]: "ready",
  [WorkflowStage.Shipped]: "delivery",
  [WorkflowStage.Delivered]: "delivered",
}

export type OrderWorkflowViewModel = {
  progress: OrderWorkflowProgress
  currentStepKey: WorkflowStage
  currentStepLabel: string
  currentStepNumber: number
  totalSteps: number
  progressLabel: string
  screenReaderSummary: string
}

export function getWorkflowStageIconKey(stageKey: string): WorkflowStageIconKey {
  const key = stageKey as WorkflowStage
  return WORKFLOW_STAGE_ICON_KEY[key] ?? "order"
}

/**
 * Customer-facing workflow label aligned with stepper terminology.
 * Single source of truth for header badge + workflow UI.
 */
export function getOrderWorkflowCustomerLabel(
  orderStatus: OrderStatus | null | undefined,
  options?: OrderWorkflowOptions
): string {
  const presentation = getOrderWorkflowPresentation(orderStatus)

  if (presentation.mode === "terminal") {
    return getOrderTerminalStatusLabel(presentation.terminalStatus)
  }

  const viewModel = getOrderWorkflowViewModel(orderStatus, options)
  return viewModel.currentStepLabel
}

/**
 * Workflow presentation metadata for stepper UI (progress label, screen reader copy).
 */
export function getOrderWorkflowViewModel(
  orderStatus: OrderStatus | null | undefined,
  options?: OrderWorkflowOptions
): OrderWorkflowViewModel {
  const workflowSteps = workflowStepsFor(options)
  const progress = getOrderWorkflowProgress(orderStatus, options)
  const totalSteps = workflowSteps.length
  const currentStep = progress.isComplete
    ? workflowSteps[totalSteps - 1]!
    : (progress.steps.find((s) => s.state === "current") ?? workflowSteps[0]!)
  const currentStepNumber = progress.isComplete ? totalSteps : progress.activeStepIndex + 1
  const currentStepKey = currentStep.key as WorkflowStage
  const completedSummary = options?.isPickup ? "Order collected" : "Order delivered"

  return {
    progress,
    currentStepKey,
    currentStepLabel: currentStep.label,
    currentStepNumber,
    totalSteps,
    progressLabel: `Step ${currentStepNumber} of ${totalSteps}`,
    screenReaderSummary: progress.isComplete
      ? `${completedSummary}, step ${totalSteps} of ${totalSteps}`
      : `Current step: ${currentStep.label}, step ${currentStepNumber} of ${totalSteps}`,
  }
}

function visualStateForIndex(
  index: number,
  activeStepIndex: number,
  isComplete: boolean
): OrderWorkflowStepVisualState {
  if (isComplete || index < activeStepIndex) return "completed"
  if (index === activeStepIndex) return "current"
  return "pending"
}

/**
 * Resolves workflow progression for the order lifecycle stepper.
 * Intended for non-terminal statuses only; terminal statuses should use {@link getOrderWorkflowPresentation}.
 */
export function getOrderWorkflowProgress(
  orderStatus: OrderStatus | null | undefined,
  options?: OrderWorkflowOptions
): OrderWorkflowProgress {
  const status = orderStatus ?? OrderStatus.Unknown
  const resolution = resolveOrderWorkflowStage(status)
  const workflowSteps = workflowStepsFor(options)
  const stepCount = workflowSteps.length

  if (resolution.kind === "terminal") {
    return {
      steps: workflowSteps.map((step) => ({ ...step, state: "pending" as const })),
      isComplete: false,
      activeStepIndex: 0,
    }
  }

  // Pickup has no dispatch step: legacy Shipped / OutForDelivery rows stay on "Ready for pickup".
  const activeStepIndex =
    resolution.kind === "complete"
      ? stepCount
      : options?.isPickup
        ? Math.min(resolution.stageIndex, PICKUP_READY_STEP_INDEX)
        : resolution.stageIndex
  const isComplete = resolution.kind === "complete"

  return {
    steps: workflowSteps.map((step, index) => ({
      ...step,
      state: visualStateForIndex(index, activeStepIndex, isComplete),
    })),
    isComplete,
    activeStepIndex,
  }
}

// ─── Legacy stage-string API (analytics / deprecated stepper) ─────────────────

export type OrderFlowStepDefinition = OrderWorkflowStepDefinition

/** @deprecated Use {@link ORDER_WORKFLOW_STEPS}. Kept for backward-compatible imports. */
export const ORDER_STATUS_FLOW_STEPS: OrderFlowStepDefinition[] = [...ORDER_WORKFLOW_STEPS]

export type OrderFlowStepState = {
  /** MUI Stepper `activeStep` (use `steps.length` when the journey is complete). */
  activeStep: number
  /** Cancelled / delivered — caller may adjust UX (alerts, non-linear display). */
  isTerminal: boolean
}

const LEGACY_STAGE_TO_STATUS: Record<string, OrderStatus> = {
  PLACED: OrderStatus.Pending,
  CONFIRMED: OrderStatus.Confirmed,
  PREPARING: OrderStatus.Preparing,
  FULFILMENT: OrderStatus.OutForDelivery,
  DELIVERED: OrderStatus.Delivered,
  CANCELLED: OrderStatus.Cancelled,
  UNKNOWN: OrderStatus.Unknown,
}

function legacyStageToOrderStatus(stage: string): OrderStatus {
  return LEGACY_STAGE_TO_STATUS[stage.toUpperCase()] ?? OrderStatus.Unknown
}

/**
 * @deprecated Prefer {@link getOrderWorkflowPresentation} with {@link OrderStatus}.
 * Resolves stepper position from legacy semantic `stage` strings.
 */
export function getOrderFlowStepState(stage: string): OrderFlowStepState {
  const presentation = getOrderWorkflowPresentation(legacyStageToOrderStatus(stage))
  if (presentation.mode === "terminal") {
    return { activeStep: 0, isTerminal: true }
  }
  const progress = getOrderWorkflowProgress(legacyStageToOrderStatus(stage))
  return {
    activeStep: progress.activeStepIndex,
    isTerminal: progress.isComplete,
  }
}
